/**
 * Secure token management — multi-session store in localStorage.
 *
 * Soltik supports up to 5 simultaneously signed-in accounts per browser
 * (mirrors the mobile app). Storage layout:
 *   `auth_sessions`    — JSON array: { userId, token, username, ... }
 *   `auth_active`      — userId of the currently active account
 *   `authToken`        — legacy single-token key, kept in sync for
 *                        existing call sites that read it directly
 *   `authTokenHash`    — integrity check on the active token
 *   `authTokenExpiry`  — 14d expiry on the active token
 *
 * `getToken()` returns the active session's token; everything else in the
 * app keeps calling it unchanged. Multi-account is additive.
 */
const TOKEN_KEY = 'authToken';                 // legacy active token
const TOKEN_HASH_KEY = 'authTokenHash';
const TOKEN_EXPIRY_KEY = 'authTokenExpiry';
const SESSIONS_KEY = 'auth_sessions';
const ACTIVE_KEY = 'auth_active';
const MAX_SESSIONS = 5;
// A partner-app-only session (soltik-api lib/beta-access.ts) — see setToken.
const OAUTH_SESSION_KEY = 'soltik-oauth-session';

export interface StoredSession {
  userId: string;
  token: string;
  username: string | null;
  displayName: string | null;
  avatarUrl: string | null;
  email: string | null;
  addedAt: number;
}

// Simple hash for integrity check (not cryptographic security, just tamper detection)
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash.toString(36);
}

function parseJwtPayload(token: string): { sub?: string; email?: string; username?: string; displayName?: string; avatar?: string; typ?: string; cid?: string; exp?: number } | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

function readSessions(): StoredSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (raw) return JSON.parse(raw) as StoredSession[];
    // Legacy migration: if only the single-token key exists, lift it into
    // a session array so multi-account works immediately.
    const legacy = localStorage.getItem(TOKEN_KEY);
    if (legacy) {
      const payload = parseJwtPayload(legacy);
      const session: StoredSession = {
        userId: payload?.sub || 'legacy',
        token: legacy,
        username: payload?.username || null,
        displayName: payload?.displayName || null,
        avatarUrl: payload?.avatar || null,
        email: payload?.email || null,
        addedAt: Date.now(),
      };
      localStorage.setItem(SESSIONS_KEY, JSON.stringify([session]));
      localStorage.setItem(ACTIVE_KEY, session.userId);
      return [session];
    }
  } catch { /* fall through */ }
  return [];
}

function writeSessions(sessions: StoredSession[]): void {
  localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
}

function writeActiveId(id: string | null): void {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

function syncLegacyKey(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_HASH_KEY, simpleHash(token));
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + 14 * 24 * 60 * 60 * 1000));
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_HASH_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }
}

/**
 * Tell the Soltik extension a user just signed in on this page.
 *
 * The extension's authSync content script (src/authSync.ts, runs only on
 * soltik.app origins) listens for this and copies the token into the
 * extension. That is how the extension signs in: its popup opens this site's
 * login page in a tab — where Turnstile, passkeys and Google all work — and
 * waits for the token to arrive. The token never leaves this origin except
 * into Soltik's own content script; no extension ID is involved, so nothing
 * else can ask for it.
 *
 * Deliberately NOT inside setToken(): admin impersonation calls setToken too,
 * and an impersonated session must never be pushed into the admin's extension.
 */
export function announceSignIn(token: string, email?: string | null): void {
  if (typeof window === 'undefined') return;
  if (parseJwtPayload(token)?.typ === 'oauth_session') return; // useless to the extension
  window.dispatchEvent(new CustomEvent('soltik-web-auth-change', {
    detail: { token, email: email ?? parseJwtPayload(token)?.email ?? '' },
  }));
}

/** Upsert a session and make it active. `profile` overrides JWT-parsed fields. */
export function setToken(
  token: string,
  profile?: Partial<Omit<StoredSession, 'token' | 'addedAt'>>,
): void {
  const payload = parseJwtPayload(token);
  // An account created through a partner app's "Sign in with soltik" without
  // a beta invite gets a session that only /oauth/authorize accepts. It is
  // kept apart from the account list, so the rest of the site still sees a
  // signed-out visitor; the authorize page reads it with getOAuthSession().
  if (payload?.typ === 'oauth_session') {
    try { localStorage.setItem(OAUTH_SESSION_KEY, token); } catch { /* storage blocked */ }
    return;
  }
  clearOAuthSession();
  const userId = profile?.userId || payload?.sub || 'unknown';
  const sessions = readSessions();
  const idx = sessions.findIndex(s => s.userId === userId);
  const next: StoredSession = {
    userId,
    token,
    username: profile?.username ?? sessions[idx]?.username ?? payload?.username ?? null,
    displayName: profile?.displayName ?? sessions[idx]?.displayName ?? payload?.displayName ?? null,
    avatarUrl: profile?.avatarUrl ?? sessions[idx]?.avatarUrl ?? payload?.avatar ?? null,
    email: profile?.email ?? sessions[idx]?.email ?? payload?.email ?? null,
    addedAt: Date.now(),
  };
  if (idx >= 0) sessions[idx] = next;
  else sessions.unshift(next);
  while (sessions.length > MAX_SESSIONS) sessions.pop();
  writeSessions(sessions);
  writeActiveId(userId);
  syncLegacyKey(token);
}

export function getToken(): string | null {
  const sessions = readSessions();
  if (sessions.length === 0) return null;
  const activeId = localStorage.getItem(ACTIVE_KEY);
  const active = sessions.find(s => s.userId === activeId) || sessions[0];

  // Integrity + expiry apply to the legacy mirror of the active token so
  // tampered tokens still get wiped.
  const legacyToken = localStorage.getItem(TOKEN_KEY);
  if (legacyToken && legacyToken === active.token) {
    const storedHash = localStorage.getItem(TOKEN_HASH_KEY);
    if (storedHash && simpleHash(legacyToken) !== storedHash) {
      clearToken();
      return null;
    }
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiry && Date.now() > parseInt(expiry)) {
      clearToken();
      return null;
    }
  } else {
    syncLegacyKey(active.token);
  }
  return active.token;
}

/** Remove the active session. If others remain, promote the next one. */
export function clearToken(): void {
  const sessions = readSessions();
  const activeId = localStorage.getItem(ACTIVE_KEY);
  const remaining = sessions.filter(s => s.userId !== activeId);
  writeSessions(remaining);
  if (remaining.length > 0) {
    writeActiveId(remaining[0].userId);
    syncLegacyKey(remaining[0].token);
  } else {
    writeActiveId(null);
    syncLegacyKey(null);
    localStorage.removeItem(SESSIONS_KEY);
  }
}

/** The partner-app-only session for this client, if one is stored and unexpired. */
export function getOAuthSession(clientId: string): { token: string; label: string | null } | null {
  let token: string | null;
  try { token = localStorage.getItem(OAUTH_SESSION_KEY); } catch { return null; }
  if (!token) return null;
  const p = parseJwtPayload(token);
  if (!p || p.typ !== 'oauth_session' || p.cid !== clientId || typeof p.exp !== 'number' || p.exp * 1000 <= Date.now()) {
    return null;
  }
  return { token, label: p.displayName || p.username || p.email || null };
}

export function clearOAuthSession(): void {
  try { localStorage.removeItem(OAUTH_SESSION_KEY); } catch { /* storage blocked */ }
}

/** Forget every session in this browser's store (the partner domain's store, see partner/site.ts). */
export function clearAllSessions(): void {
  try { localStorage.removeItem(SESSIONS_KEY); } catch { /* storage blocked */ }
  writeActiveId(null);
  syncLegacyKey(null);
  clearOAuthSession();
}

export function listSessions(): StoredSession[] {
  return readSessions();
}

export function getActiveUserId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export function switchSession(userId: string): boolean {
  const sessions = readSessions();
  const s = sessions.find(x => x.userId === userId);
  if (!s) return false;
  writeActiveId(userId);
  syncLegacyKey(s.token);
  return true;
}

export function removeSession(userId: string): void {
  const sessions = readSessions();
  const next = sessions.filter(s => s.userId !== userId);
  writeSessions(next);
  const activeId = localStorage.getItem(ACTIVE_KEY);
  if (activeId === userId) {
    if (next.length > 0) {
      writeActiveId(next[0].userId);
      syncLegacyKey(next[0].token);
    } else {
      writeActiveId(null);
      syncLegacyKey(null);
      localStorage.removeItem(SESSIONS_KEY);
    }
  }
}
